import { initializeApp } from 'firebase/app'
import {
  GoogleAuthProvider,
  getAuth,
  isSignInWithEmailLink,
  onAuthStateChanged,
  sendSignInLinkToEmail,
  signInWithEmailLink,
  signInWithPopup,
  signOut,
} from 'firebase/auth'
import type { AuthProvider, AuthenticatedUser } from './types'

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
}

const isFirebaseConfigured = Object.values(firebaseConfig).every(Boolean)

const app = isFirebaseConfigured ? initializeApp(firebaseConfig) : null
export const auth = app ? getAuth(app) : null

const provider = new GoogleAuthProvider()
provider.setCustomParameters({ prompt: 'select_account' })

function subscribeAuth(listener: (user: AuthenticatedUser | null) => void) {
  if (!auth) {
    listener(null)
    return () => undefined
  }
  return onAuthStateChanged(auth, listener)
}

async function login() {
  if (!auth) throw new Error('Firebase is not configured')
  await signInWithPopup(auth, provider)
}

async function authenticate(email: string) {
  if (!auth) throw new Error('Firebase is not configured')
  await sendSignInLinkToEmail(auth, email, {
    url: window.location.origin,
    handleCodeInApp: true,
  })
  window.localStorage.setItem('sandbox-control-plane-email', email)
}

function hasSignInLink() {
  return Boolean(auth && isSignInWithEmailLink(auth, window.location.href))
}

async function finishSignInLink() {
  if (!auth) throw new Error('Firebase is not configured')
  const email = window.localStorage.getItem('sandbox-control-plane-email')
  if (!email) throw new Error('Open the sign-in link in the same browser session.')
  await signInWithEmailLink(auth, email, window.location.href)
  window.localStorage.removeItem('sandbox-control-plane-email')
  window.history.replaceState({}, document.title, window.location.origin)
}

async function logout() {
  if (!auth) return
  await signOut(auth)
}

export const firebaseAuthProvider: AuthProvider = {
  mode: 'email',
  isConfigured: isFirebaseConfigured,
  subscribe: subscribeAuth,
  login,
  authenticate,
  hasSignInLink,
  finishSignInLink,
  logout,
}
